import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Box, Paper, Typography } from "@mui/material";

/**
 * undefined  → not wrapped in ReportPageLayout (caller must keep actions visible)
 * null       → wrapped, toolbar slot not mounted yet (suppress to avoid wrong-place flash)
 * HTMLElement → portal target in the title row
 *
 * The title-row slot is shared: every <ReportToolbar> portals into the same node.
 * If more than one mounts, children appear in mount order (do not rely on more than one).
 */
const ReportToolbarTargetContext = createContext(undefined);

/**
 * Title row (left) + toolbar slot (right), then report body in Paper.
 * Export/actions from report bodies should render through <ReportToolbar>.
 */
export function ReportPageLayout({ title, classes, children }) {
  const [toolbarNode, setToolbarNode] = useState(null);

  const setToolbarRef = useCallback((node) => {
    setToolbarNode((prev) => (prev === node ? prev : node));
  }, []);

  return (
    <ReportToolbarTargetContext.Provider value={toolbarNode}>
      <div id="react-no-print" className={classes.main}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            marginLeft: "1rem",
            marginRight: "1rem",
            minHeight: 44,
          }}
        >
          <Typography
            variant="display1"
            className={classes.typography}
            sx={{ marginLeft: "0 !important", marginBottom: 0, flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          <Box
            ref={setToolbarRef}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          />
        </Box>
        {/*
          Paper is intentional App chrome: most report bodies do not wrap themselves.
          A few reports also render an inner Paper (pre-existing); removing those inners
          would be a larger behavioral change than this layout ticket warrants.
          marginTop tightened vs classes.paper so the title+toolbar row sits closer to the grid.
        */}
        <Paper className={classes.paper} sx={{ marginTop: "8px" }}>
          {children}
        </Paper>
      </div>
    </ReportToolbarTargetContext.Provider>
  );
}

/**
 * Places export/actions in the report title row when under ReportPageLayout.
 * If rendered outside that layout, falls back to inline so actions never disappear.
 * Multiple instances share one slot and append in mount order — use a single toolbar per page.
 */
export function ReportToolbar({ children }) {
  const target = useContext(ReportToolbarTargetContext);

  if (target === undefined) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  // First render under ReportPageLayout: ref callback has not run yet, so target is null.
  // Returning null here avoids a one-frame flash of actions in the body; the next render portals.
  if (!target) {
    return null;
  }

  return createPortal(children, target);
}

export default ReportPageLayout;



// WEBPACK FOOTER //
// ./src/components/reports/ReportPageLayout.js